import { Disclosure } from '@headlessui/react';
import { ChevronRightIcon, DocumentTextIcon } from '@heroicons/react/24/outline';
import { Agent } from '../../types';
import { ActivePromptCard } from './ActivePromptCard';
import { EditableCompactConfig } from './EditableCompactConfig';
import { AgentKnowledgeBasesList } from './AgentKnowledgeBasesList';
import { SchemaBuilder } from '../schema-builder/SchemaBuilder';
import { useFlag } from '../../hooks/useFeatureFlags';

interface AgentOverviewTabProps {
  agent: Agent;
  onChangePrompt: () => void;
  isEditing?: boolean;
  onFieldChange?: (field: string, value: any) => void;
  onAddMcp?: () => void;
  /** Opens the knowledge-base attachment picker (task 574). */
  onAttachKnowledgeBase?: () => void;
}

export function AgentOverviewTab({
  agent,
  onChangePrompt,
  isEditing = false,
  onFieldChange,
  onAddMcp,
  onAttachKnowledgeBase,
}: AgentOverviewTabProps) {
  // Standing knowledge sits beside the agent's other reach decisions (LLM,
  // tools, MCP connections) rather than in its own tab — same shape of choice.
  const knowledgeAttachmentEnabled = useFlag('innovate.fe.knowledge.agent-attachment');
  const hasMetadata = agent.metadata && Object.keys(agent.metadata).length > 0;

  return (
    <div className="space-y-6">
      {/* Active Prompt Card */}
      <ActivePromptCard
        agentId={agent.id}
        onChangePrompt={onChangePrompt}
      />

      {/* Input/Output Schemas - side by side */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Input Schema */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          {agent.input || isEditing ? (
            <SchemaBuilder
              value={agent.input}
              onChange={(schema) => onFieldChange?.('input', schema)}
              readOnly={!isEditing}
              label="Input Schema"
            />
          ) : (
            <>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Input Schema</h3>
              <div className="text-center py-6">
                <DocumentTextIcon className="h-8 w-8 text-gray-400 dark:text-gray-500 mx-auto mb-2" />
                <p className="text-sm font-medium text-gray-900 dark:text-white mb-1">
                  No input schema defined
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Define a schema to specify the expected input structure.
                </p>
              </div>
            </>
          )}
        </div>

        {/* Output Schema */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          {agent.output || isEditing ? (
            <SchemaBuilder
              value={agent.output}
              onChange={(schema) => onFieldChange?.('output', schema)}
              readOnly={!isEditing}
              label="Output Schema"
            />
          ) : (
            <>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Output Schema</h3>
              <div className="text-center py-6">
                <DocumentTextIcon className="h-8 w-8 text-gray-400 dark:text-gray-500 mx-auto mb-2" />
                <p className="text-sm font-medium text-gray-900 dark:text-white mb-1">
                  No output schema defined
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Define a schema to specify the expected output structure.
                </p>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Configuration - LLM, Tools, MCP */}
      <EditableCompactConfig
        agent={agent}
        isEditing={isEditing}
        onFieldChange={onFieldChange}
        onAddMcp={onAddMcp}
      />

      {/* Standing knowledge — the bases this agent always searches (task 574) */}
      {knowledgeAttachmentEnabled && (
        <AgentKnowledgeBasesList
          agentId={agent.id}
          onAddKnowledgeBase={() => onAttachKnowledgeBase?.()}
        />
      )}

      {/* Metadata - Collapsible, only shown if metadata exists */}
      {hasMetadata && (
        <Disclosure>
          {({ open }) => (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
              <Disclosure.Button className="flex w-full items-center justify-between p-4 text-left">
                <h2 className="text-lg font-medium text-gray-900 dark:text-white">Metadata</h2>
                <ChevronRightIcon
                  className={`h-5 w-5 text-gray-500 transition-transform ${open ? 'rotate-90' : ''}`}
                />
              </Disclosure.Button>
              <Disclosure.Panel className="px-4 pb-4">
                <pre className="bg-gray-50 dark:bg-gray-900 p-2 rounded overflow-x-auto text-xs text-gray-900 dark:text-white">
                  {JSON.stringify(agent.metadata, null, 2)}
                </pre>
              </Disclosure.Panel>
            </div>
          )}
        </Disclosure>
      )}
    </div>
  );
}
